import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  InnerSideBarContainer,
  PageInfo,
  StyledButtonGroup,
  ToggleInnerButton
} from './SourcePage.styles';
import { generatePath, useSearchParams } from 'react-router-dom';
import { useCompanyServices } from 'presentation/hooks';
import { CircularProgress, styled } from '@mui/material';
import { ActionButton, Tooltip } from 'presentation/components';
import { routes } from 'main/router';
import {
  ALPHABETS,
  evaluate,
  formatFormula,
  formatNumber,
  getFormulaObject,
  getOperators,
  getSeriesIdsFromFormula
} from './utils';
import { Fundamental, Series } from 'domain/models';
import { ChevronLeft } from '@material-ui/icons';
import { Mixpanel } from 'infra/mixpanel';

const TableWrapper = styled('div')({
  width: '100%',

  '& > table': {
    background: '#FFFFFF',
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
    fontSize: '0.75rem',

    '& thead': {
      '& tr th': {
        fontWeight: 400
      }
    },

    '& tr': {
      border: '1px solid #0000001F',
      borderLeft: 0,
      borderRight: 0,

      '& th, & td': {
        textAlign: 'right',
        padding: '0.5rem 1rem',

        '&:first-of-type': {
          textAlign: 'left',
          wordBreak: 'break-word'
        },
        '&:last-of-type': {
          paddingLeft: 0
        }
      },

      '& td': {
        '&.value': {
          minWidth: 'fit-content',
          whiteSpace: 'nowrap'
        },

        '& a': {
          color: '#000000DE'
        }
      }
    },

    '& .operator': {
      width: '8px',
      paddingLeft: 0,
      paddingRight: 0,

      '& span': {
        color: '#D32F2F',
        fontWeight: 700
      }
    }
  }
});

const TableRow = styled('tr')<{ selected?: boolean }>(({ selected }) => ({
  background: selected ? '#2196F314' : '#FFFFFF',
  outline: selected ? '1px solid #2196F380' : 'none',

  '& td, & a': {
    fontWeight: selected ? 700 : 400
  }
}));

export type SideBarRef = {
  collpased: boolean;
  loading: boolean;
  collapse: () => void;
  expand: () => void;
  toggle: () => void;
};

type Props = {
  selectedFundamental: string;
  onFundamentalClick: (id: string) => void;
};

export const RolledUpSideBar = forwardRef<SideBarRef, Props>(
  ({ selectedFundamental, onFundamentalClick }, ref) => {
    const [search, setSearch] = useSearchParams();

    const companyId = search.get('company_id') ?? '';
    const period = search.get('period') ?? '';
    const formulaString = search.get('formula');
    const isYTD = search.get('isYTD') === 'true';

    const seriesIds = useMemo(() => (formulaString ? getSeriesIdsFromFormula(formulaString) : []), [
      formulaString
    ]);

    const formulaWithVariables = useMemo(() => {
      if (!formulaString) return '';
      return formatFormula(formulaString);
    }, [formulaString]);

    const formula = useMemo(() => {
      if (!formulaWithVariables) return;
      return getFormulaObject(formulaWithVariables);
    }, [formulaWithVariables]);

    const operators = useMemo(() => getOperators(formula), [formula]);

    const [collpased, setCollapsed] = useState(false);

    const { data: companyData, loading: isLoading } = useCompanyServices().companyDetails({
      variables: { companyId },
      skip: !companyId
    });

    const { data, loading } = useCompanyServices().seriesFundamental({
      variables: { companyId, periods: [period], seriesIds },
      skip: !companyId || !seriesIds.length || !period
    });

    const pageLoading = loading || isLoading || !companyData;

    useImperativeHandle(ref, () => ({
      collpased,
      loading: pageLoading,
      collapse: () => {
        setCollapsed(true);
      },
      expand: () => {
        setCollapsed(false);
      },
      toggle: () => {
        setCollapsed((prev) => !prev);
      }
    }));

    const seriesById = useMemo(() => {
      const seriesById: { [id: string]: Series } = {};
      data?.modelLivePreviewSeries.series.forEach((series) => {
        seriesById[series.id] = series;
      });
      return seriesById;
    }, [data?.modelLivePreviewSeries.series]);

    const fundamentals = useMemo(
      () =>
        data?.modelLivePreviewSeries.series.map(({ name, fundamentals }) => {
          return {
            label: name,
            fundamental: fundamentals.find(
              (fundamental) => fundamental.period === period && fundamental.id !== '-1'
            )
          };
        }) ?? [],
      [data?.modelLivePreviewSeries.series, period]
    );

    const resultRow = useMemo(() => {
      if (!data?.modelLivePreviewSeries.series || !formula) {
        return {};
      }

      let label = String(formulaWithVariables);

      seriesIds.forEach((seriesId, index) => {
        label = label.replace(`[${ALPHABETS[index]}${seriesId}]`, seriesById[seriesId]?.name ?? '');
      });
      const fundamentalsBySeriesId: { [id: string]: Fundamental[] } = {};
      data.modelLivePreviewSeries.series.forEach((series) => {
        fundamentalsBySeriesId[series.id] = series.fundamentals;
      });

      const value = (() => {
        if (!formula) return;

        const number = formatNumber(evaluate(formula, fundamentalsBySeriesId, seriesIds, isYTD));
        return isFinite(parseFloat(number)) ? number : 'No Result';
      })();

      return {
        label,
        value
      };
    }, [
      data?.modelLivePreviewSeries.series,
      formula,
      formulaWithVariables,
      seriesIds,
      isYTD,
      seriesById
    ]);

    const handleTogglePanel = () => {
      Mixpanel.track('marketplace:src:rollup:toggle-panel');
      setCollapsed((prev) => !prev);
    };

    const handleToggleYTD = () => {
      setSearch((prev) => {
        const isYTD = prev.get('isYTD') === 'true';
        Mixpanel.track('marketplace:src:rollup:value-toggle', { isYTD: !isYTD });
        prev.set('isYTD', String(!isYTD));
        return prev;
      });
    };

    const handleFundamentalClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      const id = e.currentTarget.id;
      onFundamentalClick(id);
      Mixpanel.track('marketplace:src:rollup:fundamental-click', { id });

      if (e.button === 0) {
        e.preventDefault();
      }
    };

    useEffect(() => {
      Mixpanel.track('marketplace:src:rollup:open', { companyId, period, formulaString });
    }, [companyId, formulaString, period]);

    if (pageLoading)
      return (
        <InnerSideBarContainer>
          <CircularProgress size={16} style={{ margin: ' 32px auto' }} />
        </InnerSideBarContainer>
      );

    const { name, ticker } = companyData?.company;

    return (
      <InnerSideBarContainer collapsed={collpased} fundamentalSelected={!!selectedFundamental}>
        <div>
          {!!selectedFundamental && false && (
            <ToggleInnerButton collapsed={collpased} onClick={handleTogglePanel}>
              <ChevronLeft />
            </ToggleInnerButton>
          )}
          <PageInfo>
            Rolled-up Number
            <Tooltip title={`${ticker} - ${name}`}>
              <span>
                {ticker} - {name}
              </span>
            </Tooltip>
            <div>
              <StyledButtonGroup>
                <ActionButton bgLight={isYTD} onClick={handleToggleYTD} data-active={!isYTD}>
                  Quarterized
                </ActionButton>
                <ActionButton bgLight={!isYTD} onClick={handleToggleYTD} data-active={isYTD}>
                  YTD
                </ActionButton>
              </StyledButtonGroup>
            </div>
          </PageInfo>
          <TableWrapper>
            <table>
              <colgroup>
                <col style={{ width: 'auto' }} />
                <col style={{ width: '8px' }} />
                <col style={{ width: '90px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Row Label</th>
                  <th className="operator" />
                  <th>{period}</th>
                </tr>
              </thead>
              <tbody>
                {fundamentals.map(({ label, fundamental }, index) => (
                  <TableRow key={index} selected={fundamental?.id === selectedFundamental}>
                    <td>{label}</td>
                    <td className="operator">
                      {!!fundamental && <span>{operators[index - 1]}</span>}
                    </td>
                    <td className="value">
                      {!fundamental && 'Not Released'}
                      {!!fundamental && (
                        <a
                          id={fundamental?.id}
                          href={generatePath(routes.SOURCE, { id: fundamental?.id })}
                          target="_blank"
                          rel="noreferrer"
                          onClick={handleFundamentalClick}
                        >
                          {fundamental?.value}
                        </a>
                      )}
                    </td>
                  </TableRow>
                ))}
                <tr>
                  <td>{resultRow.label}</td>
                  <td className="operator">
                    <span>=</span>
                  </td>
                  <td>{resultRow.value}</td>
                </tr>
              </tbody>
            </table>
          </TableWrapper>
        </div>
      </InnerSideBarContainer>
    );
  }
);
